import type {ParserT} from '@freckle/parser'
import {field, record, firstOf, merge, literal, number, onSelf, obfuscated} from '@freckle/parser'
import type {ExponentialGraphAnswerT} from '@freckle/educator-entities/ts/math/common/logic/answer-types'
import type {ExponentialEquationCoefficientsT} from '@freckle/educator-entities/ts/math/common/logic/equation'
import {validatePointsForExponentialEquation} from '@freckle/educator-entities/ts/math/common/logic/equation'
import type {MathQuestionMetadataAttrs} from '@freckle/educator-entities/ts/math/common/models/questions'
import type {CommonMathGraphQuestionAttrs} from '@freckle/educator-entities/ts/math/common/models/questions/common-parser'
import {parseCommonMathGraphQuestionAttrs} from '@freckle/educator-entities/ts/math/common/models/questions/common-parser'

type MathQuestionGraphExponentialExtraAttrs = {
  graphExponentialEquationCoefficients: ExponentialEquationCoefficientsT
}

const rawGraphExponentialEquationCoefficientsAttrs: ParserT<ExponentialEquationCoefficientsT> =
  record({
    coefficientA: field(number(), 'coefficient-a'),
    coefficientB: field(number(), 'coefficient-b'),
    coefficientC: field(number(), 'coefficient-c')
  })

const parseGraphExponentialEquationCoefficients: ParserT<MathQuestionGraphExponentialExtraAttrs> =
  firstOf(
    record({
      graphExponentialEquationCoefficients: field(
        rawGraphExponentialEquationCoefficientsAttrs,
        'graph-exponential-equation-coefficients'
      )
    }),
    record({
      graphExponentialEquationCoefficients: field(
        obfuscated(rawGraphExponentialEquationCoefficientsAttrs),
        'obfuscated-graph-exponential-equation-coefficients'
      )
    })
  )

export type MathQuestionGraphExponentialMetadataAttrs = CommonMathGraphQuestionAttrs &
  MathQuestionGraphExponentialExtraAttrs

const parseMathQuestionGraphExponentialAttrs: ParserT<MathQuestionGraphExponentialMetadataAttrs> =
  merge(parseCommonMathGraphQuestionAttrs, parseGraphExponentialEquationCoefficients)

export const parseMathQuestionGraphExponentialMetadata: ParserT<MathQuestionMetadataAttrs> = record(
  {
    answerType: field(literal('graph_exponential'), 'answer-type'),
    content: onSelf(parseMathQuestionGraphExponentialAttrs)
  }
)

// Return :: Double (either 0 or 100)
export function getAnswerCorrectness(
  metadata: MathQuestionGraphExponentialMetadataAttrs,
  answer: ExponentialGraphAnswerT
): number {
  const {graphExponentialEquationCoefficients} = metadata
  const isCorrect = validatePointsForExponentialEquation(
    answer.points,
    graphExponentialEquationCoefficients
  )
  return isCorrect ? 100 : 0
}
